import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import './PricingPage.css';
import LoginPage from './LoginPage';
import PaymentAbandonSurvey from './PaymentAbandonSurvey';
import { useAuth } from './useAuth';
import { getFirestoreInstance, initializationPromise } from './firebase';
import {
  trackPricingPageVisit,
  trackPricingRegionShown,
  trackSubscribeNowClick,
  trackCancelSubscriptionWhatsAppClick,
  trackCouponCodeApplied,
  trackReferralInviteClicked,
  trackPricingFaqItemOpened,
} from './userJourneyTracker';
import { readCachedVisitorIsIndia, prefetchVisitorCountry } from './visitorCountry';
import {
  buildReferralLandingUrl,
  buildReferralShareMessage,
  persistPendingReferralCoupon,
  REFERRAL_SHARER_COPY_CODE,
} from './referralShare';

const includedItems = [
  { icon: 'check', text: 'Unlimited video processing (no credits system)' },
  { icon: 'check', text: 'Add Scroll stopping AI Intro hooks' },
  { icon: 'check', text: 'Daily Personalised Content ideas' },
  { icon: 'check', text: 'Personalised Script based on your channel' },
  { icon: 'check', text: 'Auto silence remover' },
  { icon: 'check', text: 'Automatic Sound Effects' },
  { icon: 'check', text: 'Turn long videos into viral shorts instantly' },
  { icon: 'check', text: 'High Quality B-rolls' },
  { icon: 'check', text: 'AI powered highlight detection' },
  { icon: 'check', text: '35+ languages Support' },
  { icon: 'check', text: 'Auto reframing (perfect 9:16 format)' },
  { icon: 'check', text: 'Paste URL from - YouTube, Drive, X, Vimeo, Zoom, Reddit & more' },
  { icon: 'check', text: '24/7 Customer support' },
  { icon: 'check', text: 'Early access to new features' }
];

const freeGenerationsItems = [
  { icon: 'check', text: 'No watermark ever' },
  { icon: 'check', text: 'Export in original quality up to 4K' },
  { icon: 'check', text: 'Unlimited downloads' }
];

const unlimitedItems = [
  { icon: 'check', text: 'Create up to 6 clips from 1 video' },
  { icon: 'check', text: 'Content Scheduling to major platforms' },
  { icon: 'check', text: 'Access to 1-Tap Publish to social' },
  { icon: 'check', text: 'Auto generate Title, Description, Tags' },
  { icon: 'check', text: 'Access to Creators Streak' },
  { icon: 'check', text: 'Save hours of manual editing' },
  { icon: 'check', text: 'Stay consistent without effort' },
  { icon: 'check', text: 'Built for growth & virality' }
];

const pricingFaqItems = [
  {
    question: 'Is it actually unlimited?',
    lines: [
      'Fair question 😄',
      <>
        <strong>{"Yes, it's truly unlimited."}</strong>{' '}
        No credits, no hidden limits. If your subscription is active, you can create as many shorts as you want under a fair use policy.
      </>,
    ],
  },
  {
    question: 'Is it credit based?',
    lines: [
      'Nope. No credits, no confusing system. Just one plan that matters, use it as much as you want.',
    ],
  },
  {
    question: 'What languages are supported?',
    lines: [
      'We support 35+ languages across a wide range so you can create content for any audience. This includes English, Hindi, Spanish, French, German, Portuguese, Russian, Japanese, Arabic, Bengali, Marathi, Tamil, Telugu, Urdu, Indonesian, Turkish, Vietnamese, Italian, Dutch, Korean, and many more.',
      'If your video has clear speech, we can handle it.',
    ],
  },
  {
    question: 'Is my subscription automatically renewed?',
    lines: [
      "Yes, it is a recurring subscription so your access doesn't stop.",
      'But you can cancel anytime before the next cycle, no lock-ins.',
    ],
  },
  {
    question: 'What type of videos work best?',
    lines: [
      'It works best with videos where a person is clearly visible and speaking.',
      'Interviews, podcasts, tutorials, and talking videos usually give the best results.',
      'Videos with only background music, no speech, or unclear audio may not return any results.',
    ],
  },
  {
    question: 'How do I cancel my subscription?',
    lines: [
      <>
        You can cancel anytime from your dashboard under{' '}
        <strong>{'Pricing page > Cancel Subscription'}</strong>.
      </>,
      'You can also request cancellation via support email or WhatsApp support.',
    ],
  },
];

const FeatureRow = ({ item }) => {
  const iconMap = useMemo(
    () => ({ check: '✓', cross: '✕', inf: '∞' }),
    []
  );
  const icon = iconMap[item.icon] || '•';

  return (
    <div className="pricing-feature-row">
      <div className="pricing-feature-left">
        <span className={`pricing-feat-icon ${item.icon}`}>{icon}</span>
        <span className={`pricing-feat-text ${item.dim ? 'dim' : ''}`}>
          {item.text}
          {item.miniBadge && (
            <span className="pricing-feat-badge mini-4k">{item.miniBadge}</span>
          )}
        </span>
      </div>
      {item.badge && (
        <span className={`pricing-feat-badge ${item.badgeType}`}>{item.badge}</span>
      )}
    </div>
  );
};

const PRICING_PAGE_BODY_CLASS = 'pricing-page-no-site-header';
const RAZORPAY_CHECKOUT_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';
/** One survey prompt per browser tab session (see PaymentAbandonSurvey). */
const PAYMENT_ABANDON_SURVEY_SESSION_KEY = 'makereels_pricing_abandon_survey_v1';
const RAZORPAY_DISMISS_MIN_MS = 500;
/** Fallback if /api/limits has no whatsapp.support_number (matches App.js / WhatsAppSupportFab). */
const DEFAULT_WA_SUPPORT_DIGITS = '2104196594';
const CANCEL_SUBSCRIPTION_WHATSAPP_MESSAGE =
  'Hi, I would like to cancel my subscription.';
const parsePricingEntry = (entry) => {
  const price_old = String(entry?.price_old ?? '').trim();
  const price_new = String(entry?.price_new ?? '').trim();
  const period = String(entry?.period ?? '').trim();
  if (!price_old || !price_new || !period) return null;
  return { price_old, price_new, period };
};

const parsePricingDisplayDoc = (data) => {
  const inEntry = parsePricingEntry(data?.in);
  const intlEntry = parsePricingEntry(data?.intl);
  if (!inEntry || !intlEntry) return null;
  return { in: inEntry, intl: intlEntry };
};

const PricingPage = () => {
  const { idToken, isAuthenticated, user, loading: authLoading } = useAuth();
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [referralInviteFeedback, setReferralInviteFeedback] = useState('');
  const [openPricingFaqKeys, setOpenPricingFaqKeys] = useState(() => new Set());
  const [pricingInviteCardEnabled, setPricingInviteCardEnabled] = useState(true);
  /** India vs intl display row. First paint uses sessionStorage if App prefetch already ran. */
  const [visitorIsIndia, setVisitorIsIndia] = useState(
    () => readCachedVisitorIsIndia() ?? true
  );
  const [isAnnual, setIsAnnual] = useState(true);
  const [isBillingEnabled, setIsBillingEnabled] = useState(false);
  const [isCheckingFeature, setIsCheckingFeature] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  /** Why checkout is blocked (set when /api/features fails or billing is off). Shown instead of login modal. */
  const [billingGateMessage, setBillingGateMessage] = useState('');
  const isPremiumUser = user?.plan_tier === 'premium';
  const subscriptionChargeAtIso = user?.subscription_charge_at_iso || '';
  const subscriptionCurrentEndIso = user?.subscription_current_end_iso || '';
  const premiumSource = user?.premium_source || '';

  const [couponCode, setCouponCode] = useState('');
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [couponTrialEndIso, setCouponTrialEndIso] = useState('');
  /** Paid coupon applied on pricing page — updates display + checkout plan. */
  const [appliedPaidCoupon, setAppliedPaidCoupon] = useState(null);
  const [isPaymentAbandonSurveyOpen, setIsPaymentAbandonSurveyOpen] = useState(false);
  const [whatsAppSupportDigits, setWhatsAppSupportDigits] = useState(DEFAULT_WA_SUPPORT_DIGITS);
  const [pricingDisplay, setPricingDisplay] = useState(null);
  /** loading until first server snapshot; ready when Firestore config is valid; error otherwise. */
  const [pricingDisplayStatus, setPricingDisplayStatus] = useState('loading');
  /** Set true synchronously when Razorpay `handler` runs so `ondismiss` never opens the survey after success. */
  const razorpayCheckoutSucceededRef = useRef(false);
  /** Skip survey when user attempted payment and Razorpay reported failure (different from “closed without paying”). */
  const razorpayPaymentFailedRef = useRef(false);

  useEffect(() => {
    document.body.classList.add(PRICING_PAGE_BODY_CLASS);
    return () => document.body.classList.remove(PRICING_PAGE_BODY_CLASS);
  }, []);

  useEffect(() => {
    trackPricingPageVisit();
  }, []);

  useEffect(() => {
    prefetchVisitorCountry().then(setVisitorIsIndia);
  }, []);

  useEffect(() => {
    let unsubscribe = null;
    let cancelled = false;

    const failPricingDisplay = () => {
      if (cancelled) return;
      setPricingDisplay(null);
      setPricingDisplayStatus('error');
    };

    initializationPromise
      .then(() => {
        if (cancelled) return;
        const db = getFirestoreInstance();
        const pricingRef = doc(db, 'config', 'pricing_display');
        unsubscribe = onSnapshot(
          pricingRef,
          { includeMetadataChanges: true },
          (snapshot) => {
            if (snapshot.metadata.fromCache) {
              return;
            }
            if (!snapshot.exists()) {
              failPricingDisplay();
              return;
            }
            const parsed = parsePricingDisplayDoc(snapshot.data() || {});
            if (!parsed) {
              failPricingDisplay();
              return;
            }
            setPricingDisplay(parsed);
            setPricingDisplayStatus('ready');
          },
          (error) => {
            console.error('Error listening to pricing display config:', error);
            failPricingDisplay();
          }
        );
      })
      .catch((error) => {
        console.error('Error initializing Firebase pricing listener:', error);
        failPricingDisplay();
      });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    fetch('/api/limits')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const raw = data?.whatsapp?.support_number;
        if (raw != null && String(raw).trim() !== '') {
          setWhatsAppSupportDigits(String(raw).replace(/\D/g, '') || DEFAULT_WA_SUPPORT_DIGITS);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const checkPricingInviteCard = async () => {
      try {
        const response = await fetch('/api/features/pricing-invite-card-enabled');
        if (response.ok) {
          const data = await response.json();
          setPricingInviteCardEnabled(data.enabled !== false);
        }
      } catch (err) {
        console.error('Error checking pricing invite card toggle:', err);
        setPricingInviteCardEnabled(true);
      }
    };
    checkPricingInviteCard();
  }, []);

  const isPricingReady = pricingDisplayStatus === 'ready' && pricingDisplay != null;
  const isPricingLoading = pricingDisplayStatus === 'loading';

  const { displayPriceOld, displayPriceNew, displayPricePeriod } = useMemo(() => {
    const regionPricing =
      isPricingReady && pricingDisplay
        ? visitorIsIndia
          ? pricingDisplay.in
          : pricingDisplay.intl
        : null;

    if (appliedPaidCoupon) {
      // Strikethrough = current site price (what they'd pay without the coupon).
      return {
        displayPriceOld: regionPricing?.price_new || '',
        displayPriceNew: appliedPaidCoupon.displayPriceNew || '',
        displayPricePeriod:
          appliedPaidCoupon.displayPeriod || regionPricing?.period || '',
      };
    }
    if (!regionPricing) {
      return { displayPriceOld: '', displayPriceNew: '', displayPricePeriod: '' };
    }
    return {
      displayPriceOld: regionPricing.price_old,
      displayPriceNew: regionPricing.price_new,
      displayPricePeriod: regionPricing.period,
    };
  }, [appliedPaidCoupon, isPricingReady, visitorIsIndia, pricingDisplay]);

  useEffect(() => {
    if (!isPricingReady) return;
    const region = visitorIsIndia ? (isAnnual ? 'IN' : 'INTL_LEGACY_ROW') : 'INTL_USD';
    const priceText = `${displayPriceNew}/${displayPricePeriod.replace(/^per\s+/i, '')}`;
    trackPricingRegionShown(region, priceText);
  }, [displayPriceNew, displayPricePeriod, isAnnual, isPricingReady, visitorIsIndia]);

  useEffect(() => {
    const loadFeatures = async () => {
      if (!isAuthenticated || !idToken) {
        setIsBillingEnabled(false);
        setBillingError('');
        setBillingGateMessage('');
        return;
      }

      setIsCheckingFeature(true);
      setBillingGateMessage('');
      try {
        const response = await fetch('/api/features', {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        });
        const bodyText = await response.text();
        let data = {};
        try {
          data = bodyText ? JSON.parse(bodyText) : {};
        } catch {
          data = {};
        }

        if (!response.ok) {
          setIsBillingEnabled(false);
          const serverErr = data.error || data.message || bodyText?.slice(0, 120);
          if (response.status === 401) {
            setBillingGateMessage(
              'Could not verify your login (401). Your session token may be expired — sign out, sign in again, then retry.'
            );
          } else if (response.status === 403) {
            setBillingGateMessage(
              serverErr
                ? `Access denied (403): ${serverErr}`
                : 'Access denied (403). Check server billing flags and RAZORPAY_ALLOWED_EMAILS.'
            );
          } else {
            setBillingGateMessage(
              serverErr
                ? `Feature check failed (${response.status}): ${serverErr}`
                : `Feature check failed (${response.status}). Check backend logs.`
            );
          }
          return;
        }

        const enabled = Boolean(data.razorpay_subscription_enabled);
        setIsBillingEnabled(enabled);
        if (data.coupon_trial_end_at_iso) {
          setCouponTrialEndIso(data.coupon_trial_end_at_iso);
        }
        if (isPremiumUser) {
          setBillingGateMessage('');
          return;
        }
        if (!enabled) {
          setBillingGateMessage(
            'Subscription checkout is disabled right now. If you manage the app: open Admin → Controls and enable Razorpay checkout, set ENABLE_RAZORPAY_SUBSCRIPTION=true on the server, and use RAZORPAY_ALLOWED_EMAILS only for a phased rollout (leave empty to allow everyone).'
          );
        }
      } catch (err) {
        setIsBillingEnabled(false);
        setBillingGateMessage(
          err?.message?.includes('fetch')
            ? 'Could not reach the API. If you use npm start, ensure the dev proxy targets your Flask backend (port 8000).'
            : `Network error: ${err?.message || 'unknown'}`
        );
      } finally {
        setIsCheckingFeature(false);
      }
    };

    loadFeatures();
  }, [isAuthenticated, idToken, isPremiumUser]);

  const handleCancelSubscriptionWhatsApp = () => {
    trackCancelSubscriptionWhatsAppClick();
    const digits = whatsAppSupportDigits || DEFAULT_WA_SUPPORT_DIGITS;
    const text = encodeURIComponent(CANCEL_SUBSCRIPTION_WHATSAPP_MESSAGE);
    const href = digits
      ? `https://wa.me/${digits}?text=${text}`
      : `https://wa.me/?text=${text}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const existing = document.querySelector(`script[src="${RAZORPAY_CHECKOUT_SCRIPT}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve(true), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay SDK')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = RAZORPAY_CHECKOUT_SCRIPT;
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
      document.body.appendChild(script);
    });

  const handleSubscribe = async () => {
    trackSubscribeNowClick();
    setBillingError('');

    if (!isPricingReady && !appliedPaidCoupon) {
      if (pricingDisplayStatus === 'error') {
        setBillingError('Pricing is unavailable right now. Please refresh the page.');
      }
      return;
    }

    if (!isAuthenticated || !idToken) {
      setIsSignupModalOpen(true);
      return;
    }

    if (isPremiumUser) {
      setBillingError('You already have an active Unlimited plan.');
      return;
    }

    if (isCheckingFeature) {
      return;
    }

    if (!isBillingEnabled) {
      setBillingError(
        billingGateMessage ||
          'Checkout is not enabled. See the note above the button or check /api/features in Network tab.'
      );
      return;
    }

    setIsCheckoutLoading(true);
    try {
      const createResponse = await fetch('/api/billing/create-subscription', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          total_count: 12,
          customer_notify: true,
          visitor_is_india: visitorIsIndia,
          ...(appliedPaidCoupon?.code ? { coupon_code: appliedPaidCoupon.code } : {}),
        }),
      });
      if (!createResponse.ok) {
        const err = await createResponse.json().catch(() => ({}));
        throw new Error(err.error || 'Unable to start subscription');
      }

      const createData = await createResponse.json();
      if (!createData.subscription_id || !createData.key_id) {
        throw new Error('Invalid subscription response');
      }

      await loadRazorpayScript();

      razorpayCheckoutSucceededRef.current = false;
      razorpayPaymentFailedRef.current = false;

      let modalOpenedAt = 0;

      const options = {
        key: createData.key_id,
        subscription_id: createData.subscription_id,
        name: 'Makereels',
        description: `Creators Plan - ${displayPriceNew}/${displayPricePeriod.replace(/^per\s+/i, '')}`,
        handler: async function (response) {
          razorpayCheckoutSucceededRef.current = true;
          try {
            const verifyResponse = await fetch('/api/billing/verify-subscription-payment', {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(response),
            });
            if (!verifyResponse.ok) {
              const err = await verifyResponse.json().catch(() => ({}));
              throw new Error(err.error || 'Payment verification failed');
            }
            window.location.href = '/?session=success';
          } catch (error) {
            setBillingError(error.message || 'Payment verification failed');
            setIsCheckoutLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setIsCheckoutLoading(false);
            if (razorpayCheckoutSucceededRef.current || razorpayPaymentFailedRef.current) {
              return;
            }
            if (!modalOpenedAt || Date.now() - modalOpenedAt < RAZORPAY_DISMISS_MIN_MS) {
              return;
            }
            try {
              if (typeof sessionStorage !== 'undefined') {
                if (sessionStorage.getItem(PAYMENT_ABANDON_SURVEY_SESSION_KEY)) {
                  return;
                }
                sessionStorage.setItem(PAYMENT_ABANDON_SURVEY_SESSION_KEY, '1');
              }
            } catch (_) {
              /* ignore quota / privacy mode */
            }
            setIsPaymentAbandonSurveyOpen(true);
          },
        },
        theme: {
          color: '#3399cc',
        },
        ...(user?.email
          ? {
              prefill: { email: user.email },
              readonly: { email: true },
            }
          : {}),
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function () {
        razorpayPaymentFailedRef.current = true;
        setBillingError('Payment failed. Please try again.');
        setIsCheckoutLoading(false);
      });
      modalOpenedAt = Date.now();
      rzp.open();
    } catch (error) {
      setBillingError(error.message || 'Unable to start checkout right now');
      setIsCheckoutLoading(false);
    }
  };

  const handleRemovePaidCoupon = () => {
    setAppliedPaidCoupon(null);
    setCouponSuccess('');
    setCouponError('');
  };

  const handleApplyCoupon = async () => {
    setCouponError('');
    setCouponSuccess('');

    if (!isAuthenticated || !idToken) {
      setIsSignupModalOpen(true);
      return;
    }

    if (isPremiumUser) {
      setCouponError('You already have Premium access.');
      return;
    }

    const normalized = String(couponCode || '').trim().toUpperCase();
    if (!normalized) {
      setCouponError('Enter a coupon code.');
      return;
    }
    trackCouponCodeApplied(normalized);

    setIsCouponLoading(true);
    try {
      const res = await fetch('/api/coupons/redeem', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: normalized,
          visitor_is_india: visitorIsIndia,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const rawError = String(data.error || 'Coupon failed');
        const normalizedError =
          rawError.toLowerCase() === 'coupon is disabled' ? 'Coupon is invalid' : rawError;
        throw new Error(normalizedError);
      }

      if (data.type === 'paid') {
        if (!isBillingEnabled) {
          throw new Error(
            billingGateMessage ||
              'Checkout is not enabled. Paid coupons require Razorpay checkout to be active.'
          );
        }
        setAppliedPaidCoupon({
          code: data.code || normalized,
          displayPriceNew: data.display_price_new || '',
          displayPeriod: data.display_period || '',
        });
        setCouponSuccess(
          data.display_price_new
            ? `Coupon applied — ${data.display_price_new}${data.display_period ? ` ${data.display_period}` : ''}. Click Subscribe Now to pay.`
            : 'Coupon applied. Click Subscribe Now to pay.'
        );
        return;
      }

      const trialEndIso = data.trial_end_at_iso || '';
      setCouponTrialEndIso(trialEndIso);
      setAppliedPaidCoupon(null);
      setCouponSuccess(
        trialEndIso
          ? `Coupon applied. Premium trial active until ${formatPlanExpiry(trialEndIso)}.`
          : 'Coupon applied. Premium trial active.'
      );

      // Firestore snapshot picks up premium; match Razorpay post-checkout success flow.
      window.location.href = '/?session=success';
    } catch (e) {
      setCouponError(e?.message || 'Coupon failed');
    } finally {
      setIsCouponLoading(false);
    }
  };

  const formatPlanExpiry = (isoString) => {
    if (!isoString) return 'N/A';
    const dt = new Date(isoString);
    if (Number.isNaN(dt.getTime())) return 'N/A';
    return dt.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getNextRenewalDate = () => subscriptionChargeAtIso || subscriptionCurrentEndIso || '';
  const effectiveCouponTrialEnd = couponTrialEndIso || user?.coupon_trial_end_at_iso || '';
  const getPremiumPlanMessage = () =>
    premiumSource === 'coupon' && effectiveCouponTrialEnd
      ? `You are already on the Premium plan. Coupon expires on : ${formatPlanExpiry(effectiveCouponTrialEnd)}`
      : `You are already on the Premium plan. Next renewal on : ${formatPlanExpiry(getNextRenewalDate())}`;

  const isSubscribeBusy =
    isCheckingFeature || isCheckoutLoading || (!isPremiumUser && isPricingLoading);
  const isSubscribeDisabled =
    isPremiumUser
      ? isCheckingFeature
      : isSubscribeBusy || (!isPricingReady && !appliedPaidCoupon);

  const canShareReferral =
    !authLoading && isAuthenticated && Boolean(idToken) && Boolean(user?.uid);

  const runReferralShare = async () => {
    setReferralInviteFeedback('');
    if (!isAuthenticated || !idToken || !user?.uid) {
      return;
    }
    const uid = user.uid;

    const landingUrl = buildReferralLandingUrl(uid);
    const message = buildReferralShareMessage(landingUrl);

    const markShared = () =>
      persistPendingReferralCoupon({ userId: uid, couponCode: REFERRAL_SHARER_COPY_CODE });

    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          await navigator.share({
            title: 'Makereels',
            text: message,
          });
          markShared();
          return;
        } catch (shareErr) {
          if (shareErr?.name === 'AbortError') {
            return;
          }
        }
      }
    } catch (_) {}

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
        markShared();
        setReferralInviteFeedback('Message copied — paste it in WhatsApp or any chat.');
        window.setTimeout(() => setReferralInviteFeedback(''), 5000);
        return;
      }
    } catch (_) {}

    const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
    markShared();
  };

  const handleInviteButtonClick = () => {
    trackReferralInviteClicked();
    if (authLoading) return;
    if (!canShareReferral) {
      setIsSignupModalOpen(true);
      return;
    }
    runReferralShare();
  };

  const togglePricingFaq = (question) => {
    setOpenPricingFaqKeys((prev) => {
      const next = new Set(prev);
      if (next.has(question)) {
        next.delete(question);
      } else {
        trackPricingFaqItemOpened(question);
        next.add(question);
      }
      return next;
    });
  };

  return (
    <div className="pricing-page">
      <div className="pricing-shell">
        <section className="pricing-hero">
          <video
            className="pricing-hero-video"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="/bgshorts.mp4" type="video/mp4" />
          </video>
          <div className="pricing-hero-bg" />
          <button
            className="pricing-back-btn"
            onClick={() => window.history.back()}
            type="button"
            aria-label="Go back"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            Back
          </button>
          <div className="pricing-hero-content">
            <p className="pricing-hero-label">Special Offer</p>
            <h1 className="pricing-hero-title">UNLIMITED</h1>
            <p className="pricing-hero-sub">Usage • No Hidden Fees</p>
          </div>
        </section>

        <section className="pricing-main-card">
          <div className="pricing-content-split">
            {/* Mobile: stacked. Desktop: 2-col top (plan | headline+features), then FAQ full-width center, then referral */}
            <div className="pricing-right-stack">
              <h2 className="pricing-section-title">ONLY PLAN MATTERS</h2>
              <p className="pricing-section-sub">Scale your creativity with higher access</p>

              <div className="pricing-trust-bar">
                <div className="pricing-avatars">
                  <img className="pricing-avatar" src="https://i.pravatar.cc/64?img=1" alt="Creator one" />
                  <img className="pricing-avatar" src="https://i.pravatar.cc/64?img=5" alt="Creator two" />
                  <img className="pricing-avatar" src="https://i.pravatar.cc/64?img=12" alt="Creator three" />
                </div>
                <span className="pricing-trust-text">Trusted by 950+ creators</span>
              </div>
            </div>

            <div className="pricing-left-stack">
              <div className="pricing-plan-card">
                <div className="pricing-popular-strip">
                  {isPremiumUser ? '✦ CURRENTLY ACTIVE' : '✦ LIMITED TIME OFFER'}
                </div>
                <div className="pricing-plan-inner">
                  <div className="pricing-plan-heading">
                    <div className="pricing-plan-name">CREATORS PLAN</div>
                  </div>
                  <div className="pricing-plan-desc">The smartest choice for creators going viral daily</div>
                  <div className="pricing-credits-box">
                    <div className="pricing-credits-row">
                      <span className="pricing-star">✦</span>
                      <span className="pricing-credits-amount">
                        {isAnnual ? 'Unlimited Usage' : '960 credits/mo.'}
                      </span>
                      <span className="pricing-x-badge">POPULAR</span>
                    </div>
                    <div className="pricing-credits-detail">
                      ~ AI Edited Clips
                      <br />
                      ~ No Hours Of Manual Editing
                      <br />
                      ~ Viral Style Polished Clips
                    </div>
                  </div>
                  <div className="pricing-price-row">
                    {isPricingLoading && !appliedPaidCoupon ? (
                      <div
                        className="pricing-price-skeleton"
                        aria-busy="true"
                        aria-label="Loading prices"
                      >
                        <span className="pricing-skeleton-line pricing-skeleton-line--old" />
                        <span className="pricing-skeleton-line pricing-skeleton-line--new" />
                        <span className="pricing-skeleton-line pricing-skeleton-line--period" />
                      </div>
                    ) : isPricingReady || appliedPaidCoupon ? (
                      <>
                        <span className="pricing-price-old">{displayPriceOld}</span>
                        <span className="pricing-price-new">{displayPriceNew}</span>
                        <span className="pricing-price-period">{displayPricePeriod}</span>
                      </>
                    ) : (
                      <p className="pricing-display-error" role="alert">
                        Pricing is unavailable. Please refresh the page.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {!isPremiumUser && billingGateMessage && (
                <p className="pricing-billing-gate-msg" role="alert">
                  {billingGateMessage}
                </p>
              )}

              <button
                className={`pricing-cta-btn${isSubscribeBusy ? ' pricing-cta-btn--loading' : ''}`}
                onClick={isPremiumUser ? handleCancelSubscriptionWhatsApp : handleSubscribe}
                type="button"
                disabled={isSubscribeDisabled}
                aria-busy={isSubscribeBusy}
              >
                {isPremiumUser ? (
                  'CANCEL SUBSCRIPTION'
                ) : isSubscribeBusy ? (
                  <span className="pricing-cta-btn-inner">
                    <span className="pricing-cta-spinner" aria-hidden="true" />
                    Loading…
                  </span>
                ) : (
                  'SUBSCRIBE NOW'
                )}
              </button>
              {!isPremiumUser && (
                <div className="pricing-price-reassurance" aria-label="Cancel anytime reassurance">
                  <img
                    className="pricing-price-reassurance-badge"
                    src="/secure-payment-badge.png"
                    alt="Secure payment"
                    loading="lazy"
                  />
                  <span className="pricing-price-reassurance-text">
                    Cancel Anytime. No Questions Asked.
                  </span>
                </div>
              )}
              {!isPremiumUser && (
                <div className="pricing-coupon-box">
                  <div className="pricing-coupon-row">
                    <input
                      className="pricing-coupon-input"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Coupon Code"
                      autoComplete="off"
                      spellCheck="false"
                      disabled={isCouponLoading || isSubscribeBusy}
                    />
                    <button
                      className="pricing-coupon-apply"
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={isCouponLoading || isSubscribeBusy}
                    >
                      {isCouponLoading ? 'Checking…' : 'Apply'}
                    </button>
                  </div>
                  {couponSuccess && <p className="pricing-coupon-msg success">{couponSuccess}</p>}
                  {couponError && <p className="pricing-coupon-msg error">{couponError}</p>}
                  {appliedPaidCoupon && (
                    <div className="pricing-coupon-remove-wrap">
                      <button
                        type="button"
                        className="pricing-coupon-remove"
                        onClick={handleRemovePaidCoupon}
                        disabled={isCouponLoading || isSubscribeBusy}
                      >
                        Remove coupon
                      </button>
                    </div>
                  )}
                  {couponTrialEndIso && (
                    <p className="pricing-coupon-msg subtle">
                      Trial ends: {formatPlanExpiry(couponTrialEndIso)}
                    </p>
                  )}
                </div>
              )}
              {isPremiumUser && (
                <p className="pricing-section-sub pricing-renewal-note">
                  {getPremiumPlanMessage()}
                </p>
              )}
              {billingError && <p className="pricing-section-sub pricing-billing-error">{billingError}</p>}
            </div>

            <div className="pricing-features-region">
              <div className="pricing-right-features">
                <div className="pricing-features-section pricing-whats-included-col">
                  <div className="pricing-features-label">What&apos;s included</div>
                  {includedItems.map((item) => (
                    <FeatureRow key={item.text} item={item} />
                  ))}
                </div>

                <div className="pricing-features-col-right">
                  <div className="pricing-features-section">
                    <div className="pricing-features-label">Export &amp; Quality</div>
                    {freeGenerationsItems.map((item) => (
                      <FeatureRow key={item.text} item={item} />
                    ))}
                  </div>
                  <div className="pricing-features-section">
                    <div className="pricing-features-label">Creator Advantage</div>
                    {unlimitedItems.map((item) => (
                      <FeatureRow key={item.text} item={item} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <section className="pricing-faq" aria-labelledby="pricing-faq-heading">
              <h2 id="pricing-faq-heading" className="pricing-faq-title">
                FAQ
              </h2>
              <div className="pricing-faq-list">
                {pricingFaqItems.map((item, faqIndex) => {
                  const isFaqOpen = openPricingFaqKeys.has(item.question);
                  const faqPanelId = `pricing-faq-panel-${faqIndex}`;
                  const faqHeaderId = `pricing-faq-q-${faqIndex}`;
                  return (
                    <div
                      key={item.question}
                      className={`pricing-faq-item${isFaqOpen ? ' is-open' : ''}`}
                    >
                      <button
                        type="button"
                        id={faqHeaderId}
                        className="pricing-faq-q"
                        aria-expanded={isFaqOpen}
                        aria-controls={faqPanelId}
                        onClick={() => togglePricingFaq(item.question)}
                      >
                        {item.question}
                      </button>
                      <div
                        id={faqPanelId}
                        className="pricing-faq-a-outer"
                        role="region"
                        aria-labelledby={faqHeaderId}
                        aria-hidden={!isFaqOpen}
                      >
                        <div className="pricing-faq-a">
                          <div className="pricing-faq-a-pad">
                            {item.lines.map((line, idx) => (
                              <p key={idx} className="pricing-faq-p">
                                {line}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {pricingInviteCardEnabled && (
              <div className="pricing-referral-region">
                <div className="pricing-referral-block">
                  <div className="pricing-referral-copy">
                    <h2 className="pricing-referral-title">Invite Friends</h2>
                    <p className="pricing-referral-sub">
                      You both get 1 week of <b>Free Premium</b>
                    </p>
                  </div>
                  <button
                    type="button"
                    className="pricing-referral-invite-btn"
                    onClick={handleInviteButtonClick}
                    disabled={authLoading}
                  >
                    <span className="pricing-referral-invite-btn-inner">
                      <svg
                        className="pricing-referral-share-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M22 2L11 13"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22 2L15 22L11 13L2 9L22 2Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Invite
                    </span>
                  </button>
                </div>
                {referralInviteFeedback && (
                  <p className="pricing-referral-feedback" role="status">
                    {referralInviteFeedback}
                  </p>
                )}
              </div>
            )}
          </div>

        </section>
      </div>

      <footer className="pricing-footer">
        <div className="pricing-footer-content">
          <p className="pricing-footer-text">© 2026 Makereels.live. All rights reserved.</p>
        </div>
      </footer>

      <LoginPage
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
      />

      <PaymentAbandonSurvey
        isOpen={isPaymentAbandonSurveyOpen}
        onClose={() => setIsPaymentAbandonSurveyOpen(false)}
        idToken={idToken}
      />
    </div>
  );
};

export default PricingPage;
